/**
 * The holdout readout on a campaign's page — slice 61. Fetches; the drawing is
 * `HoldoutReadoutView`'s.
 *
 * Asked for only when the campaign held people back, which the recipient list
 * already knows: a held-back count of zero means there is nothing to read, and
 * an extra request on every campaign page would buy an "off". A failed read
 * draws nothing — the readout is advice about a campaign that has already
 * gone, and the page around it must not break over it.
 *
 * ## ONLY FOR A WORKSPACE WITH SENSE — SLICE 65
 *
 * Holdouts are Sense's measurement. The two Sense widgets on the schedule step
 * — `ScheduleHoldoutControl` (slice 61) and `SchedulePressureNotice` (slice
 * 59) — have drawn nothing without the `sense` feature since they shipped;
 * this card did not, so a
 * workspace without Sense could be shown a readout, linking to a Sense screen
 * its sidebar does not offer, for a campaign that held people back while it
 * still had the plan. Slice 65 wired Sense into the Connect screens on one
 * rule, and this is the third place it applies: no `sense`, no card, and no
 * request either — the gate is asked before the read, as the schedule
 * widgets ask it, using the same session check they use.
 *
 * The campaign's own figures still say how many were held back (the skip
 * breakdown counts them under their reason), so nothing a sender needs to
 * understand their send goes with the card. Entitlement stays advisory: the
 * route itself does not refuse, and this is a display rule, not a lock.
 */
import { useAuth } from '../../auth/auth-context';
import { fetchHoldoutReadout } from '../../lib/api/sense';
import { useAsyncData } from '../../lib/use-async-data';
import { HoldoutReadoutView } from './HoldoutReadoutView';

export function CampaignHoldoutCard({ campaignId, held }: { campaignId: string; held: number }) {
  const { user } = useAuth();
  const entitled = user !== null && user.features.includes('sense');
  if (!entitled || held === 0) return null;
  return <Readout campaignId={campaignId} />;
}

function Readout({ campaignId }: { campaignId: string }) {
  const { data } = useAsyncData((signal) => fetchHoldoutReadout(campaignId, signal), [campaignId]);
  return data === null ? null : <HoldoutReadoutView readout={data} />;
}
